import React, { useEffect, useState, useMemo, useRef } from 'react';
import { api } from '../api';
import { useToast } from '../ToastContext';
import { useAuth } from '../AuthContext';

function providerLabel(p) {
  const x = String(p || '').toLowerCase();
  if (x === 'gmail') return 'Gmail';
  if (x === 'microsoft' || x === 'outlook') return 'Outlook';
  if (x === 'imap') return 'IMAP';
  return p || 'Email';
}

export default function Audit({ rfqId }) {
  const [decisions, setDecisions] = useState([]);
  const [details, setDetails] = useState([]); // chronological list of full audit objects
  const [checkpointIdx, setCheckpointIdx] = useState(0); // 0..details.length-1, 0 = first decision
  const [exportBusy, setExportBusy] = useState(null);
  const [notifyBulkBusy, setNotifyBulkBusy] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [connectors, setConnectors] = useState([]);
  const [selectedConnectorId, setSelectedConnectorId] = useState(null);
  const playTimerRef = useRef(null);
  const toast = useToast();
  const { user } = useAuth();

  // Load connected mailboxes once and pick a sensible default
  useEffect(() => {
    api.listConnectors()
      .then((r) => (r?.items || r || []).filter((c) => c.status === 'connected'))
      .then((conns) => {
        setConnectors(conns);
        const prefId = user?.preferences?.defaultDispatchConnectorId;
        if (prefId && conns.find((c) => String(c._id) === String(prefId))) {
          setSelectedConnectorId(String(prefId));
        } else if (conns.length > 0) {
          setSelectedConnectorId(String(conns[0]._id));
        }
      })
      .catch(() => setConnectors([]));
  }, [user?.preferences?.defaultDispatchConnectorId]);

  async function notifyAllSuppliers() {
    if (!window.confirm('Send order-confirmation emails to every winning supplier on this RFQ?')) return;
    setNotifyBulkBusy(true);
    try {
      const r = await api.sendOrderConfirmation(rfqId, { connectorId: selectedConnectorId || undefined });
      const sent = (r.results || []).filter((x) => x.sent).length;
      const failed = (r.results || []).filter((x) => !x.sent);
      if (failed.length === 0) {
        toast.push(`Order confirmations sent to ${sent} supplier${sent !== 1 ? 's' : ''}`);
      } else {
        const detail = failed.slice(0, 2).map((f) => `${f.supplierId.slice(-6)}: ${f.error}`).join(' · ');
        toast.push(`Sent ${sent} of ${r.totalSuppliers} · ${failed.length} failed (${detail})`, {
          variant: failed.length === r.totalSuppliers ? 'error' : 'warning',
        });
      }
    } catch (e) {
      toast.push(e.message || 'Bulk notify failed', { variant: 'error' });
    } finally {
      setNotifyBulkBusy(false);
    }
  }

  // The "current" detail is the one at the checkpoint slider position.
  const detail = details[checkpointIdx] || null;

  async function downloadAudit(format) {
    if (!detail) return;
    setExportBusy(format);
    try {
      await api.download(api.auditExportUrl(detail._id, format), `decision-${detail._id}.${format === 'html' ? 'html' : 'json'}`);
    } catch (e) {
      toast.push(e.message || 'Export failed', { variant: 'error' });
    } finally {
      setExportBusy(null);
    }
  }

  // Load all decisions + their full audit detail so the timeline can render
  // cumulative state at every checkpoint without re-fetching on each scrub.
  useEffect(() => {
    let cancelled = false;
    api.listDecisions(rfqId).then(async (r) => {
      const sorted = (r.items || []).slice().sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
      if (cancelled) return;
      setDecisions(sorted);
      if (sorted.length === 0) return;
      // Fetch full audit details in parallel
      const all = await Promise.all(sorted.map((d) => api.audit(d._id)));
      if (cancelled) return;
      setDetails(all);
      setCheckpointIdx(all.length - 1); // start at the latest checkpoint
    });
    return () => { cancelled = true; };
  }, [rfqId]);

  // Auto-play through checkpoints
  useEffect(() => {
    if (!playing) {
      if (playTimerRef.current) { clearTimeout(playTimerRef.current); playTimerRef.current = null; }
      return;
    }
    if (checkpointIdx >= details.length - 1) { setPlaying(false); return; }
    playTimerRef.current = setTimeout(() => setCheckpointIdx((i) => Math.min(i + 1, details.length - 1)), 1200);
    return () => { if (playTimerRef.current) clearTimeout(playTimerRef.current); };
  }, [playing, checkpointIdx, details.length]);

  // Cumulative awards at each checkpoint, keyed by lineId.
  // cumulativeByCheckpoint[i] = { [lineId]: { quantity, supplierName, ... } } (latest pick per supplier wins)
  const cumulative = useMemo(() => {
    const out = [];
    const byLine = {}; // lineId -> array of { supplierId, supplierName, quantity, unitPrice, decidedAtIdx }
    for (let i = 0; i < details.length; i++) {
      const d = details[i];
      for (const sel of d.selections || []) {
        const lid = String(sel.rfqLineItemId?._id || sel.rfqLineItemId);
        if (!byLine[lid]) byLine[lid] = [];
        byLine[lid].push({
          supplierId: String(sel.supplierId?._id || sel.supplierId),
          supplierName: sel.supplierId?.name || String(sel.supplierId).slice(-6),
          quantity: Number(sel.quantity) || 0,
          unitPrice: Number(sel.quoteLineId?.unitPrice) || 0,
          rfqLine: sel.rfqLineItemId,
          decidedAtIdx: i,
        });
      }
      out.push(JSON.parse(JSON.stringify(byLine)));
    }
    return out;
  }, [details]);

  // Diff vs previous checkpoint: which selections were *added* in this decision?
  const newSelectionKeys = useMemo(() => {
    if (!detail) return new Set();
    const keys = new Set();
    for (const sel of detail.selections || []) {
      const lid = String(sel.rfqLineItemId?._id || sel.rfqLineItemId);
      const sid = String(sel.supplierId?._id || sel.supplierId);
      keys.add(`${lid}::${sid}`);
    }
    return keys;
  }, [detail]);

  if (decisions.length === 0) return <div className="empty"><h3>No decisions committed</h3></div>;
  if (!detail) return <div className="empty">Loading audit…</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <h2 className="card-title" style={{ margin: 0 }}>Decision audit trail</h2>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            className="select"
            value={selectedConnectorId || ''}
            onChange={(e) => setSelectedConnectorId(e.target.value || null)}
            style={{ fontSize: 12, maxWidth: 240 }}
            title="Send order confirmations from this mailbox"
          >
            {connectors.length === 0 && <option value="">CNERZY default sender</option>}
            {connectors.map((c) => (
              <option key={c._id} value={c._id}>
                {providerLabel(c.provider)} · {c.displayEmail || c.imapUser || c.email || c._id.slice(-6)}
              </option>
            ))}
          </select>
          <button className="btn btn-secondary btn-sm" disabled={notifyBulkBusy} onClick={notifyAllSuppliers}>
            {notifyBulkBusy ? 'Sending…' : 'Notify all winning suppliers'}
          </button>
          <button className="btn btn-secondary btn-sm" disabled={exportBusy === 'json'} onClick={() => downloadAudit('json')}>
            {exportBusy === 'json' ? 'Preparing…' : 'Export JSON'}
          </button>
          <button className="btn btn-sm" disabled={exportBusy === 'html'} onClick={() => downloadAudit('html')}>
            {exportBusy === 'html' ? 'Preparing…' : 'Open PDF'}
          </button>
        </div>
      </div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
          <div><b>Mode</b><div>{detail.mode}</div></div>
          <div><b>Decided at</b><div>{new Date(detail.decidedAt || detail.createdAt).toLocaleString()}</div></div>
          <div><b>User</b><div>{detail.userId?.name || detail.userId?.email}</div></div>
          <div><b>Hash</b><div className="mono" style={{ wordBreak: 'break-all', fontSize: 11 }}>{detail.hash}</div></div>
        </div>
        {detail.overallReason && <div style={{ marginTop: 12 }}><b>Overall reason</b><div>{detail.overallReason}</div></div>}
      </div>

      {/* Decision timeline (scrubbable) */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h3 className="card-title" style={{ margin: 0 }}>Decision timeline</h3>
          <span className="chip muted" style={{ fontSize: 11 }}>{details.length} checkpoint{details.length !== 1 ? 's' : ''}</span>
          <div style={{ flex: 1 }} />
          <button
            className="btn btn-ghost btn-sm"
            disabled={checkpointIdx <= 0}
            onClick={() => { setPlaying(false); setCheckpointIdx((i) => Math.max(0, i - 1)); }}
          >‹ Prev</button>
          <button
            className="btn btn-sm"
            disabled={details.length <= 1}
            onClick={() => {
              if (checkpointIdx >= details.length - 1) setCheckpointIdx(0);
              setPlaying((p) => !p);
            }}
          >{playing ? '❚❚ Pause' : '▶ Play'}</button>
          <button
            className="btn btn-ghost btn-sm"
            disabled={checkpointIdx >= details.length - 1}
            onClick={() => { setPlaying(false); setCheckpointIdx((i) => Math.min(details.length - 1, i + 1)); }}
          >Next ›</button>
        </div>

        {/* Horizontal timeline */}
        <div style={{ position: 'relative', padding: '20px 8px 8px' }}>
          {/* Connector line */}
          <div style={{ position: 'absolute', top: 32, left: 16, right: 16, height: 2, background: 'var(--border-2)' }} />
          <div style={{ position: 'absolute', top: 32, left: 16, height: 2, background: 'var(--primary)', width: details.length > 1 ? `calc(${(checkpointIdx / (details.length - 1)) * 100}% - ${32 / Math.max(details.length - 1, 1)}px)` : 0, transition: 'width 600ms ease' }} />
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            {details.map((d, i) => {
              const active = i === checkpointIdx;
              const past = i < checkpointIdx;
              return (
                <button
                  key={d._id}
                  type="button"
                  onClick={() => { setPlaying(false); setCheckpointIdx(i); }}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    color: active ? 'var(--primary)' : past ? 'var(--ink-700)' : 'var(--ink-500)',
                  }}
                  title={`Decision ${i + 1} · ${new Date(d.decidedAt || d.createdAt).toLocaleString()}`}
                >
                  <span style={{
                    width: active ? 16 : 12,
                    height: active ? 16 : 12,
                    borderRadius: '50%',
                    background: past || active ? 'var(--primary)' : '#fff',
                    border: '2px solid ' + (past || active ? 'var(--primary)' : 'var(--border-2)'),
                    boxShadow: active ? '0 0 0 4px rgba(26,26,82,0.15)' : 'none',
                    transition: 'all 300ms ease',
                  }} />
                  <span style={{ fontSize: 10, fontWeight: active ? 600 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 90 }}>
                    #{i + 1} {d.mode}
                  </span>
                  <span style={{ fontSize: 10, color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
                    {new Date(d.decidedAt || d.createdAt).toLocaleDateString()}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cumulative state at this checkpoint */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h3 className="card-title" style={{ margin: 0 }}>State after checkpoint #{checkpointIdx + 1}</h3>
          <span className="chip info" style={{ fontSize: 11 }}>cumulative</span>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>Line</th>
              <th>Part</th>
              <th>Required</th>
              <th>Awarded so far</th>
              <th>Suppliers</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const snap = cumulative[checkpointIdx] || {};
              // Aggregate per line: build a stable list from the union of all lines that have ever been awarded
              const lineIds = Object.keys(snap);
              if (lineIds.length === 0) return <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--ink-500)', padding: 24 }}>No selections at this point.</td></tr>;
              return lineIds.map((lid) => {
                const arr = snap[lid];
                // Latest pick per supplier wins (later decisions for the same supplier overwrite)
                const latestBySupplier = {};
                for (const a of arr) latestBySupplier[a.supplierId] = a;
                const picks = Object.values(latestBySupplier);
                const lineRef = picks[0]?.rfqLine || {};
                const required = Number(lineRef.quantity) || 0;
                const awardedQty = picks.reduce((acc, p) => acc + (p.quantity || 0), 0);
                const isComplete = required > 0 && awardedQty >= required;
                const remainingQty = Math.max(0, required - awardedQty);
                return (
                  <tr key={lid}>
                    <td>{lineRef.lineNumber || lid.slice(-6)}</td>
                    <td className="mono">{lineRef.partNumber || '—'}</td>
                    <td className="mono">{required || '—'}</td>
                    <td className="mono">{awardedQty}</td>
                    <td>
                      {picks.map((p, i) => {
                        const justAdded = newSelectionKeys.has(`${lid}::${p.supplierId}`) && p.decidedAtIdx === checkpointIdx;
                        return (
                          <div key={i} style={{ fontSize: 12, marginBottom: 2, padding: justAdded ? '2px 6px' : 0, background: justAdded ? 'var(--primary-tint)' : 'transparent', borderRadius: 4, display: 'inline-block', marginRight: 6 }}>
                            <b>{p.supplierName}</b>
                            <span className="mono" style={{ color: 'var(--ink-500)', marginLeft: 6 }}>{p.quantity} pcs</span>
                            {p.unitPrice ? <span className="mono" style={{ color: 'var(--ink-500)', marginLeft: 6 }}>@ ${p.unitPrice.toFixed(2)}</span> : null}
                            {justAdded && <span className="chip primary" style={{ fontSize: 10, marginLeft: 6 }}>+ this checkpoint</span>}
                          </div>
                        );
                      })}
                    </td>
                    <td>
                      {isComplete
                        ? <span className="chip ready" style={{ fontSize: 11 }}>Complete</span>
                        : <span className="chip review" style={{ fontSize: 11 }}>{remainingQty} remaining</span>}
                    </td>
                  </tr>
                );
              });
            })()}
          </tbody>
        </table>
      </div>

      {/* This checkpoint's raw selections */}
      <div className="card">
        <h3 className="card-title">This decision's selections ({detail.selections.length})</h3>
        <table className="table">
          <thead>
            <tr>
              <th>Line</th>
              <th>Part</th>
              <th>Supplier</th>
              <th>Qty awarded</th>
              <th>Unit price</th>
              <th>Line total</th>
              <th>Reason</th>
              <th>Risks accepted</th>
            </tr>
          </thead>
          <tbody>
            {detail.selections.map((s, i) => {
              const requiredQty = Number(s.rfqLineItemId?.quantity) || 0;
              const awardedQty = Number(s.quantity) || requiredQty;
              const unitPrice = Number(s.quoteLineId?.unitPrice) || 0;
              const lineTotal = awardedQty * unitPrice;
              const isPartial = requiredQty > 0 && awardedQty < requiredQty;
              return (
                <tr key={i}>
                  <td>{s.rfqLineItemId?.lineNumber}</td>
                  <td className="mono">{s.rfqLineItemId?.partNumber}</td>
                  <td>{s.supplierId?.name}</td>
                  <td className="mono">
                    {awardedQty}/{requiredQty || '—'}
                    {isPartial && <span className="chip review" style={{ fontSize: 10, marginLeft: 6 }}>partial</span>}
                  </td>
                  <td className="mono">{unitPrice ? `$${unitPrice.toFixed(2)}` : '—'}</td>
                  <td className="mono">{lineTotal ? `$${lineTotal.toFixed(2)}` : '—'}</td>
                  <td>{s.reason}</td>
                  <td>{(s.riskFlagsAccepted || []).join(', ') || '—'}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} style={{ fontWeight: 600 }}>Total</td>
              <td className="mono" style={{ fontWeight: 600 }}>
                {detail.selections.reduce((acc, s) => acc + (Number(s.quantity) || Number(s.rfqLineItemId?.quantity) || 0), 0)} pcs
              </td>
              <td />
              <td className="mono" style={{ fontWeight: 600 }}>
                ${detail.selections.reduce((acc, s) => {
                  const qty = Number(s.quantity) || Number(s.rfqLineItemId?.quantity) || 0;
                  return acc + qty * (Number(s.quoteLineId?.unitPrice) || 0);
                }, 0).toFixed(2)}
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
